import type { HeadingColor, HeadingWeight } from '@freelancer/ui/heading';
import {
  FontColor,
  FontStyle,
  FontWeight,
  TextAlign,
  TextSize,
  TextTransform,
  TextVerticalAlign,
} from '@freelancer/ui/text';
import { cva } from 'class-variance-authority';

export const TEXT_WEIGHT_TAILWIND_VARIANTS: {
  [key in FontWeight | HeadingWeight]: string;
} = {
  [FontWeight.LIGHT]: 'font-light',
  [FontWeight.NORMAL]: 'font-normal',
  [FontWeight.MEDIUM]: 'font-medium',
  [FontWeight.BOLD]: 'font-bold',
  [FontWeight.BLACK]: 'font-black',
  [FontWeight.INHERIT]: 'font-weight-inherit',
};

export const TEXT_WEIGHT_TAILWIND_TABLET_VARIANTS: {
  [key in FontWeight | HeadingWeight]: string;
} = {
  [FontWeight.LIGHT]: 'tablet:font-light',
  [FontWeight.NORMAL]: 'tablet:font-normal',
  [FontWeight.MEDIUM]: 'tablet:font-medium',
  [FontWeight.BOLD]: 'tablet:font-bold',
  [FontWeight.BLACK]: 'tablet:font-black',
  [FontWeight.INHERIT]: 'tablet:font-weight-inherit',
};

export const TEXT_COLOR_TAILWIND_VARIANTS: {
  [key in FontColor | HeadingColor]: string;
} = {
  [FontColor.FOREGROUND]: 'text-foreground',
  [FontColor.DARK]: 'text-dark',
  [FontColor.LIGHT]: 'text-light',
  [FontColor.PRIMARY]: 'text-primary',
  [FontColor.SUCCESS]: 'text-success',
  [FontColor.WARNING]: 'text-warning',
  [FontColor.ERROR]: 'text-error',
  [FontColor.INHERIT]: 'text-color-inherit',
};

export const TEXT_SIZE_TAILWIND_VARIANTS: { [key in TextSize]: string } = {
  [TextSize.INHERIT]: 'text-size-inherit',
  [TextSize.TITLE_90]: 'text-title-90',
  [TextSize.TITLE_60]: 'text-title-60',
  [TextSize.TITLE_50]: 'text-title-50',
  [TextSize.TITLE_40]: 'text-title-40',
  [TextSize.TITLE_32]: 'text-title-32',
  [TextSize.TITLE_24]: 'text-title-24',
  [TextSize.TITLE_20]: 'text-title-20',
  [TextSize.TITLE_16]: 'text-title-16',
  [TextSize.SUBTITLE_24]: 'text-subtitle-24',
  [TextSize.SUBTITLE_20]: 'text-subtitle-20',
  [TextSize.SUBTITLE_16]: 'text-subtitle-16',
  [TextSize.BODY_24]: 'text-body-24',
  [TextSize.BODY_20]: 'text-body-20',
  [TextSize.BODY_16]: 'text-body-16',
  [TextSize.BODY_12]: 'text-body-12',
  [TextSize.BUTTON_24]: 'text-button-24',
  [TextSize.BUTTON_20]: 'text-button-20',
  [TextSize.BUTTON_16]: 'text-button-16',
  [TextSize.BUTTON_12]: 'text-button-12',
  [TextSize.LINK_20]: 'text-link-20',
  [TextSize.LINK_16]: 'text-link-16',
  [TextSize.LINK_12]: 'text-link-12',
};

export const TEXT_SIZE_TAILWIND_TABLET_VARIANTS: {
  [key in TextSize]: string;
} = {
  [TextSize.INHERIT]: 'tablet:text-size-inherit',
  [TextSize.TITLE_90]: 'tablet:text-title-90',
  [TextSize.TITLE_60]: 'tablet:text-title-60',
  [TextSize.TITLE_50]: 'tablet:text-title-50',
  [TextSize.TITLE_40]: 'tablet:text-title-40',
  [TextSize.TITLE_32]: 'tablet:text-title-32',
  [TextSize.TITLE_24]: 'tablet:text-title-24',
  [TextSize.TITLE_20]: 'tablet:text-title-20',
  [TextSize.TITLE_16]: 'tablet:text-title-16',
  [TextSize.SUBTITLE_24]: 'tablet:text-subtitle-24',
  [TextSize.SUBTITLE_20]: 'tablet:text-subtitle-20',
  [TextSize.SUBTITLE_16]: 'tablet:text-subtitle-16',
  [TextSize.BODY_24]: 'tablet:text-body-24',
  [TextSize.BODY_20]: 'tablet:text-body-20',
  [TextSize.BODY_16]: 'tablet:text-body-16',
  [TextSize.BODY_12]: 'tablet:text-body-12',
  [TextSize.BUTTON_24]: 'tablet:text-button-24',
  [TextSize.BUTTON_20]: 'tablet:text-button-20',
  [TextSize.BUTTON_16]: 'tablet:text-button-16',
  [TextSize.BUTTON_12]: 'tablet:text-button-12',
  [TextSize.LINK_20]: 'tablet:text-link-20',
  [TextSize.LINK_16]: 'tablet:text-link-16',
  [TextSize.LINK_12]: 'tablet:text-link-12',
};

export const TEXT_SIZE_TAILWIND_DESKTOP_VARIANTS: {
  [key in TextSize]: string;
} = {
  [TextSize.INHERIT]: 'desktop:text-size-inherit',
  [TextSize.TITLE_90]: 'desktop:text-title-90',
  [TextSize.TITLE_60]: 'desktop:text-title-60',
  [TextSize.TITLE_50]: 'desktop:text-title-50',
  [TextSize.TITLE_40]: 'desktop:text-title-40',
  [TextSize.TITLE_32]: 'desktop:text-title-32',
  [TextSize.TITLE_24]: 'desktop:text-title-24',
  [TextSize.TITLE_20]: 'desktop:text-title-20',
  [TextSize.TITLE_16]: 'desktop:text-title-16',
  [TextSize.SUBTITLE_24]: 'desktop:text-subtitle-24',
  [TextSize.SUBTITLE_20]: 'desktop:text-subtitle-20',
  [TextSize.SUBTITLE_16]: 'desktop:text-subtitle-16',
  [TextSize.BODY_24]: 'desktop:text-body-24',
  [TextSize.BODY_20]: 'desktop:text-body-20',
  [TextSize.BODY_16]: 'desktop:text-body-16',
  [TextSize.BODY_12]: 'desktop:text-body-12',
  [TextSize.BUTTON_24]: 'desktop:text-button-24',
  [TextSize.BUTTON_20]: 'desktop:text-button-20',
  [TextSize.BUTTON_16]: 'desktop:text-button-16',
  [TextSize.BUTTON_12]: 'desktop:text-button-12',
  [TextSize.LINK_20]: 'desktop:text-link-20',
  [TextSize.LINK_16]: 'desktop:text-link-16',
  [TextSize.LINK_12]: 'desktop:text-link-12',
};

export const TEXT_SIZE_TAILWIND_DESKTOP_LARGE_VARIANTS: {
  [key in TextSize]: string;
} = {
  [TextSize.INHERIT]: 'desktop_large:text-size-inherit',
  [TextSize.TITLE_90]: 'desktop_large:text-title-90',
  [TextSize.TITLE_60]: 'desktop_large:text-title-60',
  [TextSize.TITLE_50]: 'desktop_large:text-title-50',
  [TextSize.TITLE_40]: 'desktop_large:text-title-40',
  [TextSize.TITLE_32]: 'desktop_large:text-title-32',
  [TextSize.TITLE_24]: 'desktop_large:text-title-24',
  [TextSize.TITLE_20]: 'desktop_large:text-title-20',
  [TextSize.TITLE_16]: 'desktop_large:text-title-16',
  [TextSize.SUBTITLE_24]: 'desktop_large:text-subtitle-24',
  [TextSize.SUBTITLE_20]: 'desktop_large:text-subtitle-20',
  [TextSize.SUBTITLE_16]: 'desktop_large:text-subtitle-16',
  [TextSize.BODY_24]: 'desktop_large:text-body-24',
  [TextSize.BODY_20]: 'desktop_large:text-body-20',
  [TextSize.BODY_16]: 'desktop_large:text-body-16',
  [TextSize.BODY_12]: 'desktop_large:text-body-12',
  [TextSize.BUTTON_24]: 'desktop_large:text-button-24',
  [TextSize.BUTTON_20]: 'desktop_large:text-button-20',
  [TextSize.BUTTON_16]: 'desktop_large:text-button-16',
  [TextSize.BUTTON_12]: 'desktop_large:text-button-12',
  [TextSize.LINK_20]: 'desktop_large:text-link-20',
  [TextSize.LINK_16]: 'desktop_large:text-link-16',
  [TextSize.LINK_12]: 'desktop_large:text-link-12',
};

export const TEXT_SIZE_TAILWIND_DESKTOP_XLARGE_VARIANTS: {
  [key in TextSize]: string;
} = {
  [TextSize.INHERIT]: 'desktop_xlarge:text-size-inherit',
  [TextSize.TITLE_90]: 'desktop_xlarge:text-title-90',
  [TextSize.TITLE_60]: 'desktop_xlarge:text-title-60',
  [TextSize.TITLE_50]: 'desktop_xlarge:text-title-50',
  [TextSize.TITLE_40]: 'desktop_xlarge:text-title-40',
  [TextSize.TITLE_32]: 'desktop_xlarge:text-title-32',
  [TextSize.TITLE_24]: 'desktop_xlarge:text-title-24',
  [TextSize.TITLE_20]: 'desktop_xlarge:text-title-20',
  [TextSize.TITLE_16]: 'desktop_xlarge:text-title-16',
  [TextSize.SUBTITLE_24]: 'desktop_xlarge:text-subtitle-24',
  [TextSize.SUBTITLE_20]: 'desktop_xlarge:text-subtitle-20',
  [TextSize.SUBTITLE_16]: 'desktop_xlarge:text-subtitle-16',
  [TextSize.BODY_24]: 'desktop_xlarge:text-body-24',
  [TextSize.BODY_20]: 'desktop_xlarge:text-body-20',
  [TextSize.BODY_16]: 'desktop_xlarge:text-body-16',
  [TextSize.BODY_12]: 'desktop_xlarge:text-body-12',
  [TextSize.BUTTON_24]: 'desktop_xlarge:text-button-24',
  [TextSize.BUTTON_20]: 'desktop_xlarge:text-button-20',
  [TextSize.BUTTON_16]: 'desktop_xlarge:text-button-16',
  [TextSize.BUTTON_12]: 'desktop_xlarge:text-button-12',
  [TextSize.LINK_20]: 'desktop_xlarge:text-link-20',
  [TextSize.LINK_16]: 'desktop_xlarge:text-link-16',
  [TextSize.LINK_12]: 'desktop_xlarge:text-link-12',
};

export const TEXT_SIZE_TAILWIND_DESKTOP_XXL_VARIANTS: {
  [key in TextSize]: string;
} = {
  [TextSize.INHERIT]: 'desktop_xxlarge:text-size-inherit',
  [TextSize.TITLE_90]: 'desktop_xxlarge:text-title-90',
  [TextSize.TITLE_60]: 'desktop_xxlarge:text-title-60',
  [TextSize.TITLE_50]: 'desktop_xxlarge:text-title-50',
  [TextSize.TITLE_40]: 'desktop_xxlarge:text-title-40',
  [TextSize.TITLE_32]: 'desktop_xxlarge:text-title-32',
  [TextSize.TITLE_24]: 'desktop_xxlarge:text-title-24',
  [TextSize.TITLE_20]: 'desktop_xxlarge:text-title-20',
  [TextSize.TITLE_16]: 'desktop_xxlarge:text-title-16',
  [TextSize.SUBTITLE_24]: 'desktop_xxlarge:text-subtitle-24',
  [TextSize.SUBTITLE_20]: 'desktop_xxlarge:text-subtitle-20',
  [TextSize.SUBTITLE_16]: 'desktop_xxlarge:text-subtitle-16',
  [TextSize.BODY_24]: 'desktop_xxlarge:text-body-24',
  [TextSize.BODY_20]: 'desktop_xxlarge:text-body-20',
  [TextSize.BODY_16]: 'desktop_xxlarge:text-body-16',
  [TextSize.BODY_12]: 'desktop_xxlarge:text-body-12',
  [TextSize.BUTTON_24]: 'desktop_xxlarge:text-button-24',
  [TextSize.BUTTON_20]: 'desktop_xxlarge:text-button-20',
  [TextSize.BUTTON_16]: 'desktop_xxlarge:text-button-16',
  [TextSize.BUTTON_12]: 'desktop_xxlarge:text-button-12',
  [TextSize.LINK_20]: 'desktop_xxlarge:text-link-20',
  [TextSize.LINK_16]: 'desktop_xxlarge:text-link-16',
  [TextSize.LINK_12]: 'desktop_xxlarge:text-link-12',
};

export const TEXT_TRANSFORM_TAILWIND_VARIANTS: {
  [key in TextTransform]: string;
} = {
  [TextTransform.UPPERCASE]: 'uppercase',
  [TextTransform.LOWERCASE]: 'lowercase',
  [TextTransform.CAPITALIZE]: 'capitalize',
  [TextTransform.INHERIT]: 'text-transform-inherit',
};

export const TEXT_ALIGN_TAILWIND_VARIANTS: {
  [key in TextAlign]: string;
} = {
  [TextAlign.LEFT]: 'text-left',
  [TextAlign.RIGHT]: 'text-right',
  [TextAlign.CENTER]: 'text-center',
};

export const TEXT_ALIGN_TAILWIND_TABLET_VARIANTS: {
  [key in TextAlign]: string;
} = {
  [TextAlign.LEFT]: 'tablet:text-left',
  [TextAlign.RIGHT]: 'tablet:text-right',
  [TextAlign.CENTER]: 'tablet:text-center',
};

export const TEXT_ALIGN_TAILWIND_DESKTOP_VARIANTS: {
  [key in TextAlign]: string;
} = {
  [TextAlign.LEFT]: 'desktop:text-left',
  [TextAlign.RIGHT]: 'desktop:text-right',
  [TextAlign.CENTER]: 'desktop:text-center',
};

export const TEXT_FONT_STYLE_TAILWIND_VARIANTS: {
  [key in FontStyle]: string;
} = {
  [FontStyle.ITALIC]: 'italic',
  [FontStyle.NORMAL]: 'not-italic',
  [FontStyle.INHERIT]: 'font-style-inherit',
};

export const TEXT_VERTICAL_ALIGN_TAILWIND_VARIANTS: {
  [key in TextVerticalAlign]: string;
} = {
  [TextVerticalAlign.TOP]: 'align-top',
  [TextVerticalAlign.MIDDLE]: 'align-middle',
  [TextVerticalAlign.BOTTOM]: 'align-bottom',
  [TextVerticalAlign.BASELINE]: 'align-baseline',
};

export const textVariants = cva('', {
  variants: {
    align: TEXT_ALIGN_TAILWIND_VARIANTS,
    alignTablet: TEXT_ALIGN_TAILWIND_TABLET_VARIANTS,
    alignDesktop: TEXT_ALIGN_TAILWIND_DESKTOP_VARIANTS,
    color: TEXT_COLOR_TAILWIND_VARIANTS,
    size: TEXT_SIZE_TAILWIND_VARIANTS,
    sizeTablet: TEXT_SIZE_TAILWIND_TABLET_VARIANTS,
    sizeDesktop: TEXT_SIZE_TAILWIND_DESKTOP_VARIANTS,
    sizeDesktopLarge: TEXT_SIZE_TAILWIND_DESKTOP_LARGE_VARIANTS,
    sizeDesktopXLarge: TEXT_SIZE_TAILWIND_DESKTOP_XLARGE_VARIANTS,
    sizeDesktopXXL: TEXT_SIZE_TAILWIND_DESKTOP_XXL_VARIANTS,
    weight: TEXT_WEIGHT_TAILWIND_VARIANTS,
    weightTablet: TEXT_WEIGHT_TAILWIND_TABLET_VARIANTS,
    transform: TEXT_TRANSFORM_TAILWIND_VARIANTS,
    fontStyle: TEXT_FONT_STYLE_TAILWIND_VARIANTS,
    verticalAlign: TEXT_VERTICAL_ALIGN_TAILWIND_VARIANTS,
  },
  defaultVariants: {},
});
